/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {Permission} from '../shared/utils/permission.enum';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {ErrorPageEnum} from '../shared/error-page/error-page.enum';
import {SpoSearchCriteriaService} from '../spo/services/spo-search-criteria.service';
import {ClaimSearchCriteriaService} from '../claim/services/claim-search-criteria.service';
import {PartNumberChangesSearchCriteriaService} from '../part-number-changes/services/part-number-changes-search-criteria.service';
import {DependencyLoadNotifierService} from '../shared/services/dependency-load-notifier/dependency-load-notifier.service';
import {UserService} from '../shared/services/user.service';
import {UserContextEnum} from '../api/ms-security/contracts/user-context.enum';

@Component({
    selector: 'app-home-page',
    templateUrl: './home-page.component.html',
    styleUrls: ['./home-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomePageComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    constructor(private user: UserService,
                private dependencyLoadNotifier: DependencyLoadNotifierService,
                private router: Router,
                private spoSearchCriteriaService: SpoSearchCriteriaService,
                private claimSearchCriteriaService: ClaimSearchCriteriaService,
                private partNumberChangesSearchCriteriaService: PartNumberChangesSearchCriteriaService) {
    }

    ngOnInit(): void {
        this.subscriptions.add(this.dependencyLoadNotifier.onReady.subscribe(() => {
            // clear all cached filters
            this.spoSearchCriteriaService.clear();
            this.claimSearchCriteriaService.clear();
            this.partNumberChangesSearchCriteriaService.clear();
            // This should be done in a guard; it's a temporary redirect until the homepage is fully implemented.
            if (this.user.hasPermissions([
                Permission.ADMIN_SPO_VIEW,
                Permission.PARTNER_SPO_VIEW,
                Permission.ADMIN_CLAIM_VIEW,
                Permission.PARTNER_CLAIM_VIEW,
                Permission.ADMIN_PNC_VIEW,
                Permission.PARTNER_PNC_VIEW,
                Permission.PLANNER_PNC_VIEW,
                Permission.PRICE_PLANNER_PNC_VIEW,
                Permission.TECHNICIAN_PNC_VIEW,
                Permission.EE_SPECIALIST_PNC_VIEW
            ])) {
                this.router.navigate(['/', 'dashboard']);
            } else if (this.user.hasPermissions([
                Permission.ADMIN_BUNDLE_VIEW,
                Permission.ADMIN_BUNDLES_PERIOD_VIEW,
                Permission.CUSTOMER_BUNDLE_VIEW,
                Permission.CUSTOMER_BUNDLES_PERIOD_VIEW
            ])) {
                this.router.navigate(['/', 'bundles']);
            } else if (this.user.hasPermissions([
                Permission.ADMIN_SETTLEMENT_VIEW,
                Permission.SUPPLIER_SETTLEMENT_VIEW
            ])) {
                this.router.navigate(['/', 'settlements', 'supplier']);
            } else if (this.user.hasPermissions([
                Permission.ADMIN_SETTLEMENT_VIEW,
                Permission.FOUR_EYES_SETTLEMENT_VIEW
            ])) {
                this.router.navigate(['/', 'settlements', 'customer']);
            } else if (this.user.hasPermissions([
                Permission.ADMIN_BUSINESS_SETTINGS_VIEW
            ])) {
                this.router.navigateByUrl('/settings/backoffice');
            } else if (this.user.hasPermissions([
                Permission.ADMIN_ROLE_VIEW,
                Permission.PARTNER_ROLE_VIEW
            ], 'any', UserContextEnum.PNW2)) {
                this.router.navigateByUrl('/settings/membership/partners');
            } else if (this.user.hasPermissions([
                Permission.ADMIN_ROLE_VIEW
            ], 'any', UserContextEnum.ASCC)) {
                this.router.navigateByUrl('/settings/membership/ascc');
            } else if (this.user.hasPermissions([
                Permission.ADMIN_ALERTS_CONFIG_VIEW
            ], 'any')) {
                this.router.navigateByUrl('/settings/alerts');
            } else {
                this.router.navigate(['/', 'error'], {queryParams: {errorType: ErrorPageEnum.FORBIDDEN}});
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
